import React, {createContext, useContext, useState, useEffect} from 'react';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useModalPopup} from '../../../../common/components/core/useModalPopup';
import {useDashboardContext} from '../DashboardContext';
import {MessageItem} from '../../types';

export type ListUnreadMessagesContext = {
  open: boolean,
  openPopup: () => void,
  closePopup: () => void,
  page: number,
  nextPage: () => void,
  prevPage: () => void,
  currentMessage: MessageItem | null,
  messages: MessageItem[],
  markMessages: (array: string[]) => void,
}

export const defaultContext: ListUnreadMessagesContext = {
  open: false,
  openPopup: () => {},
  closePopup: () => {},
  page: 0,
  nextPage: () => {},
  prevPage: () => {},
  currentMessage: null,
  messages: [],
  markMessages: async () => {}
};

export type ListUnreadMessagesContextProviderProps = {
  children: React.ReactNode,
}

export const ListUnreadMessagesContext = createContext<ListUnreadMessagesContext>(defaultContext);
ListUnreadMessagesContext.displayName = 'ListUnreadMessagesContext';

export const useListUnreadMessagesContext = () => useContext(ListUnreadMessagesContext);

export const ListUnreadMessagesContextProvider = ({children}: ListUnreadMessagesContextProviderProps) => {
    const {apiService} = useGlobalContext();
    const {unreadMessages} = useDashboardContext();
    const globalMessage = useGlobalMessageContext();
    const M = useMessage();

    const [messages, setMessages] = useState<MessageItem[]>([]);
    const handleRemoveMessages = (messageIDs: string[]) => {
      setMessages(prevState => prevState.filter(item => !messageIDs.includes(item._id)));
    };

    useEffect(() => {
      setMessages(unreadMessages);
    }, [unreadMessages]);

    useEffect(() => {
      if (messages.length === 0) {
        closePopup();
      }
    }, [messages]);

    const [page, setPage] = useState(defaultContext.page);
    const nextPage = () => setPage(prevState => Math.min(prevState + 1, messages.length - 1));
    const prevPage = () => setPage(prevState => Math.max(prevState - 1, 0));

    const {open, openPopup, closePopup} = useModalPopup();

    const markMessages = async (messageIDs: string[]) => {
      try {
        const result = await apiService.markMessageAsRead(messageIDs);

        if (result.errorCode < 0) {
          globalMessage.open(M.serverError);
        } else {
          handleRemoveMessages(messageIDs);
        }
      } catch (e) {
        globalMessage.open(M.serverError);
      }
    };

    const context: ListUnreadMessagesContext = {
      ...defaultContext,
      open,
      openPopup,
      closePopup,
      page,
      nextPage,
      prevPage,
      currentMessage: messages[page],
      messages,
      markMessages
    };

    return (
      <ListUnreadMessagesContext.Provider value={context}>
        {children}
      </ListUnreadMessagesContext.Provider>
    );
  }
;
